import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Group as GroupIcon,
  Send as SendIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenInNewIcon,
  ViewList as ViewListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import config from '../config';

const Chats = ({ token }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedChats, setSelectedChats] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [openMessagesDialog, setOpenMessagesDialog] = useState(false);
  const [messageTypes, setMessageTypes] = useState({
    all: 0,
    text: 0,
    media: 0,
  });

  const fetchChats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${config.API_BASE}/list-chats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('Chats API response:', response.data);
      
      // Handle different response formats
      let chatsData = [];
      if (response.data && response.data.chats) {
        chatsData = response.data.chats;
      } else if (Array.isArray(response.data)) {
        chatsData = response.data;
      }
      
      console.log('Parsed chats data:', chatsData);
      
      if (chatsData && Array.isArray(chatsData)) {
        const formattedChats = chatsData.map((chat, index) => ({
          id: chat.id || `chat_${index}`,
          chat_id: chat.id || `chat_${index}`,
          title: chat.title || chat.name || 'Unnamed Chat',
          type: chat.type || chat.chat_type || 'unknown',
          member_count: chat.member_count || chat.participants_count || chat.members || 0,
          username: chat.username || chat.usernames?.[0] || 'No username',
          is_verified: chat.is_verified ? 'Yes' : 'No',
          is_scam: chat.is_scam ? 'Yes' : 'No',
          last_activity: chat.last_activity || chat.last_message_date || 'Never',
          has_photo: chat.photo?.has_photo ? 'Yes' : 'No',
          dc_id: chat.photo?.dc_id || null,
          // Additional fields that might be present
          is_creator: chat.is_creator || false,
          is_admin: chat.is_admin || false,
          is_member: chat.is_member || true,
        }));
        setChats(formattedChats);
        
        // Calculate message type counts
        calculateMessageTypes(formattedChats);
      } else {
        setChats([]);
        toast.info('No chats found or unexpected format');
      }
      
    } catch (error) {
      console.error('Error fetching chats:', error);
      setError('Failed to fetch chats: ' + (error.response?.data?.detail || error.message));
      toast.error('Failed to fetch chats');
    } finally {
      setLoading(false);
    }
  };

  const calculateMessageTypes = (chatsList) => {
    // This is a placeholder - in reality you'd need to fetch messages for each chat
    // For now, we'll use dummy data or you can implement batch fetching
    setMessageTypes({
      all: chatsList.length * 100, // Example count
      text: Math.floor(chatsList.length * 70),
      media: Math.floor(chatsList.length * 30),
    });
  };

  const fetchChatMessages = async (chatId, chatType = 'all') => {
    if (!chatId) {
      toast.error('Chat ID is required');
      return;
    }

    try {
      setMessagesLoading(true);
      const response = await axios.get(
        `${config.API_BASE}/chats/list-messages/${chatType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            chat_id: typeof chatId === 'string' ? parseInt(chatId) || chatId : chatId
          }
        }
      );
      
      console.log('Messages for chat:', response.data);
      
      if (response.data && response.data.messages) {
        const formattedMessages = response.data.messages.map((msg, index) => ({
          id: msg.id || `msg_${index}`,
          message_id: msg.id,
          text: msg.text || msg.message || '(No text)',
          date: msg.date ? new Date(msg.date).toLocaleString() : 'Unknown',
          sender_id: msg.sender_id || 'Unknown',
          sender_name: msg.sender_name || 'Unknown',
          media: msg.media || false,
          reply_to: msg.reply_to || null,
          chat_id: response.data.chat_id || chatId,
        }));
        
        setChatMessages(formattedMessages);
        toast.success(`Loaded ${formattedMessages.length} messages`);
      } else {
        setChatMessages([]);
        toast.info('No messages found for this chat');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to fetch messages: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleViewMessages = (chat) => {
    setSelectedChat(chat);
    setOpenMessagesDialog(true);
    fetchChatMessages(chat.chat_id);
  };

  const handleExportExcel = () => {
    try {
      if (chats.length === 0) {
        toast.error('No chats to export');
        return;
      }

      const exportData = chats.map(chat => ({
        'Chat ID': chat.chat_id,
        'Title': chat.title,
        'Type': chat.type,
        'Username': chat.username,
        'Members': chat.member_count,
        'Verified': chat.is_verified,
        'Scam': chat.is_scam,
        'Last Activity': chat.last_activity,
        'Has Photo': chat.has_photo,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Chats');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `telegram_chats_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Chats exported successfully!');
    } catch (error) {
      toast.error('Failed to export chats');
    }
  };

  const handleExportMessagesExcel = () => {
    try {
      if (chatMessages.length === 0) {
        toast.error('No messages to export');
        return;
      }

      const exportData = chatMessages.map(msg => ({
        'Message ID': msg.message_id,
        'Chat ID': msg.chat_id,
        'Text': msg.text,
        'Date': msg.date,
        'Sender ID': msg.sender_id,
        'Sender Name': msg.sender_name,
        'Has Media': msg.media ? 'Yes' : 'No',
        'Reply To': msg.reply_to,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Messages');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `messages_chat_${selectedChat?.chat_id}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Messages exported successfully!');
    } catch (error) {
      toast.error('Failed to export messages');
    }
  };

  useEffect(() => {
    fetchChats();
  }, [token]);

  const filteredChats = chats.filter(chat =>
    (chat.title && chat.title.toLowerCase().includes(search.toLowerCase())) ||
    (chat.username && chat.username.toLowerCase().includes(search.toLowerCase())) ||
    (chat.type && chat.type.toLowerCase().includes(search.toLowerCase())) ||
    (chat.chat_id && chat.chat_id.toString().includes(search))
  );

  const columns = [
    { 
      field: 'chat_id', 
      headerName: 'Chat ID', 
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'title', 
      headerName: 'Title', 
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {params.row.type === 'group' || params.row.type === 'supergroup' ? (
            <GroupIcon fontSize="small" color="primary" />
          ) : params.row.type === 'channel' ? (
            <PublicIcon fontSize="small" color="secondary" />
          ) : (
            <ChatIcon fontSize="small" />
          )}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params.value}
            </Typography>
            {params.row.username !== 'No username' && (
              <Typography variant="caption" color="textSecondary">
                @{params.row.username}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value === 'private' ? 'rgba(59, 130, 246, 0.1)' : 
                    params.value === 'group' ? 'rgba(16, 185, 129, 0.1)' :
                    params.value === 'channel' ? 'rgba(139, 92, 246, 0.1)' : 
                    params.value === 'supergroup' ? 'rgba(245, 158, 11, 0.1)' : 
                    'rgba(100, 116, 139, 0.1)',
            color: params.value === 'private' ? '#3b82f6' :
                  params.value === 'group' ? '#10b981' :
                  params.value === 'channel' ? '#8b5cf6' :
                  params.value === 'supergroup' ? '#f59e0b' : '#64748b',
            textTransform: 'capitalize'
          }}
        />
      )
    },
    { 
      field: 'member_count', 
      headerName: 'Members', 
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PeopleIcon fontSize="small" />
          <Typography>{params.value || 'N/A'}</Typography>
        </Box>
      )
    },
    { field: 'is_verified', headerName: 'Verified', width: 90 },
    { field: 'is_scam', headerName: 'Scam', width: 80 },
    { field: 'last_activity', headerName: 'Last Active', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Messages">
            <IconButton
              size="small"
              onClick={() => handleViewMessages(params.row)}
              sx={{ color: 'primary.main' }}
            >
              <MessageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open Chat">
            <IconButton
              size="small"
              onClick={() => window.open(`https://t.me/${params.row.username}`, '_blank')}
              disabled={params.row.username === 'No username'}
              sx={{ color: 'primary.main' }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const getChatTypeCount = (type) => {
    return chats.filter(chat => chat.type === type).length;
  };

  const chatTypeStats = [
    { type: 'private', label: 'Private Chats', count: getChatTypeCount('private'), color: '#3b82f6' },
    { type: 'group', label: 'Groups', count: getChatTypeCount('group'), color: '#10b981' },
    { type: 'channel', label: 'Channels', count: getChatTypeCount('channel'), color: '#8b5cf6' },
    { type: 'supergroup', label: 'Supergroups', count: getChatTypeCount('supergroup'), color: '#f59e0b' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Chats Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchChats}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={chats.length === 0}
          >
            Export All Chats
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: 'background.paper', height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {chats.length}
              </Typography>
              <Typography color="textSecondary">Total Chats</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {chatTypeStats.map((stat, index) => (
          <Grid item xs={12} md={3} key={index}>
            <Card sx={{ 
              borderRadius: 3, 
              bgcolor: 'background.paper', 
              height: '100%',
              borderLeft: `4px solid ${stat.color}`
            }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                  {stat.count}
                </Typography>
                <Typography color="textSecondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="All Chats" />
        <Tab label="Groups" />
        <Tab label="Channels" />
        <Tab label="Private Chats" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search chats by name, username, or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <IconButton>
              <FilterIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading chats...</Typography>
            </Box>
          ) : chats.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No Chats Found
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                {token ? 'You have no chats or Telegram is not connected.' : 'Please login first.'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchChats}
              >
                Try Again
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ height: 600 }}>
                <DataGrid
                  rows={filteredChats}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  loading={loading}
                  checkboxSelection
                  onSelectionModelChange={(newSelection) => {
                    setSelectedChats(newSelection);
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                />
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color="textSecondary">
                  Total: {chats.length} chats | Showing: {filteredChats.length}
                  {search && ` (filtered by: "${search}")`}
                </Typography>
                {selectedChats.length > 0 && (
                  <Chip
                    icon={<ViewListIcon />}
                    label={`${selectedChats.length} selected`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Messages Dialog */}
      <Dialog 
        open={openMessagesDialog} 
        onClose={() => setOpenMessagesDialog(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedChat?.type === 'group' ? <GroupIcon /> : <ChatIcon />}
            <Box>
              <Typography variant="h6">{selectedChat?.title}</Typography>
              <Typography variant="body2" color="textSecondary">
                Chat ID: {selectedChat?.chat_id} • {chatMessages.length} messages
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {messagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading messages...</Typography>
            </Box>
          ) : chatMessages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <MessageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography color="textSecondary">No messages found in this chat</Typography>
            </Box>
          ) : (
            <List sx={{ width: '100%' }}>
              {chatMessages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {message.sender_name?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {message.sender_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {message.date}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{ display: 'block', mt: 1 }}
                        >
                          {message.text}
                          {message.media && (
                            <Chip
                              label="Media"
                              size="small"
                              sx={{ ml: 1 }}
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {message.reply_to && (
                            <Chip
                              label={`Reply to: ${message.reply_to}`}
                              size="small"
                              sx={{ ml: 1 }}
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < chatMessages.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenMessagesDialog(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportMessagesExcel}
            disabled={chatMessages.length === 0}
          >
            Export Messages
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedChat) {
                fetchChatMessages(selectedChat.chat_id);
              }
            }}
            disabled={messagesLoading}
          >
            Refresh Messages
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Actions Card */}
      <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<MessageIcon />}
                onClick={() => {
                  if (chats.length > 0) {
                    handleViewMessages(chats[0]);
                  } else {
                    toast.error('No chats available');
                  }
                }}
                disabled={chats.length === 0}
              >
                View First Chat
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GroupIcon />}
                onClick={() => {
                  const groups = chats.filter(c => c.type === 'group' || c.type === 'supergroup');
                  if (groups.length > 0) {
                    handleViewMessages(groups[0]);
                  } else {
                    toast.error('No groups available');
                  }
                }}
                disabled={!chats.some(c => c.type === 'group' || c.type === 'supergroup')}
              >
                View Groups
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={chats.length === 0}
              >
                Export All
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchChats}
              >
                Refresh All
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Chats;